import { RefObject, useMemo, useSyncExternalStore } from 'react'

function subscribe(callback: (e: Event) => void) {
  window.addEventListener('resize', callback)
  return () => {
    window.removeEventListener('resize', callback)
  }
}

/**
 * Allows for the dimensions of a DOM element attached to a ref to be read.
 *
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const ref = useRef(null)
 *   const {width, height} = useDimensions(ref)
 *   return <div ref={ref}>
 *     The dimensions of this div is {width} x {height}
 *   </div>
 * }
 * ```
 *
 * See https://stackoverflow.com/a/75101934 for more information.
 *
 * @param ref
 * @returns
 */
function useDimensions(ref: RefObject<HTMLElement>) {
  const dimensions = useSyncExternalStore(subscribe, () =>
    JSON.stringify({
      width: ref.current?.offsetWidth ?? 0,
      height: ref.current?.offsetHeight ?? 0,
    })
  )
  return useMemo(() => JSON.parse(dimensions), [dimensions])
}

export { useDimensions }
